import { differenceInYears } from 'date-fns';

import {
  DOB_VALIDATION_REGEX,
  EMAIL_VALIDATION_REGEX,
  PHONE_VALIDATION_REGEX,
  ZIPCODE_VALIDATION_REGEX,
} from './constants';
import { parseDate } from './stringUtils';

// *** VALIDATORS ***
export const validateEmail = (
  email: string,
): boolean => EMAIL_VALIDATION_REGEX.test(email); // eslint-disable-line

export const validatePhoneNumber = (phoneNumber: string): boolean => PHONE_VALIDATION_REGEX.test(phoneNumber); // eslint-disable-line

// TODO: maybe detect where they are from and use the correct regex for it? IDK.
export const validateDOB = (
  dob: string,
  // birthdays in MM/DD/YYYY
  ): boolean => DOB_VALIDATION_REGEX.test(dob); // eslint-disable-line

export const validateZipCode = (zipCode: string): boolean => ZIPCODE_VALIDATION_REGEX.test(zipCode);

export type EmployeeIdValidationRules = {
  validationEnabled?: boolean | null;
  allowedChars?: string | null;
  minLength?: number | null;
  maxLength?: number | null;
  errorMessage?: string | null;
};

export const validateEmployeeId = (
  employeeId: string,
  rules?: EmployeeIdValidationRules,
): { isValid: boolean; errorMessage?: string } => {
  // If validation is disabled or no rules provided, accept anything
  if (!rules?.validationEnabled) {
    return { isValid: true };
  }

  // Check if empty
  if (!employeeId || employeeId.trim() === '') {
    return {
      isValid: false,
      errorMessage: rules.errorMessage,
    };
  }

  const trimmedId = employeeId.trim();
  const idLength = trimmedId.length;

  // Validate length
  if (rules.minLength !== null && rules.minLength !== undefined && idLength < rules.minLength) {
    return {
      isValid: false,
      errorMessage: rules.errorMessage || `Employee ID must be at least ${rules.minLength} characters`,
    };
  }

  if (rules.maxLength !== null && rules.maxLength !== undefined && idLength > rules.maxLength) {
    return {
      isValid: false,
      errorMessage: rules.errorMessage || `Employee ID must be at most ${rules.maxLength} characters`,
    };
  }

  // Validate character type
  if (rules.allowedChars) {
    let regex: RegExp | null = null;

    switch (rules.allowedChars) {
    case 'numeric':
      regex = /^\d+$/;
      break;
    case 'alpha':
      regex = /^[A-Za-z]+$/;
      break;
    case 'alphanumeric':
      regex = /^[A-Za-z0-9]+$/;
      break;
    case 'any':
    default:
      // No character restrictions
      break;
    }

    if (regex && !regex.test(trimmedId)) {
      return {
        isValid: false,
        errorMessage: rules.errorMessage || 'Employee ID contains invalid characters',
      };
    }
  }

  return { isValid: true };
};

// *** TYPE GAURDS ***
// maybe this is overkill but better safe than sorrrrrrry...
type oneToNine = 1|2|3|4|5|6|7|8|9;
type zeroToNine = 0|1|2|3|4|5|6|7|8|9;
type YYYY = `19${zeroToNine}${zeroToNine}` | `20${zeroToNine}${zeroToNine}`;
type MM = `0${oneToNine}` | `1${0|1|2}`;
type DD = `${0}${oneToNine}` | `${1|2}${zeroToNine}` | `3${0|1}`
export type MMDDYYYYDateType = `${MM}/${DD}/${YYYY}`;

export const isValidMMDDYYYYDate = (str: string): str is MMDDYYYYDateType => validateDOB(str);

export const getAge = (birthDateString: string) => {
  const today = new Date();
  const birthDate = parseDate(birthDateString);

  return differenceInYears(today, birthDate);
};
